import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Upload,
  Trash2,
  Lock,
  Bell,
  Download,
  User as UserIcon,
  Check,
  X,
  Loader2,
  CreditCard,
  Building2,
  Save,
  FileSpreadsheet,
  AlertTriangle,
  Globe,
  Key,
  Copy,
  ExternalLink,
  Link2,
  FileText,
  Palette,
} from "lucide-react";
import * as XLSX from "xlsx";
import { useProfile } from "../hooks/useProfile";
import { useSubscription } from "../hooks/useSubscription";
import { supabase } from "../supabaseClient";
import { getCurrentLocale } from "../i18n";
import type { User, Job, SubscriptionStatus } from "../types";
import { InvoiceTemplateModal } from "./InvoiceTemplateModal";

interface SettingsPageProps {
  user: User;
  jobs: Job[];
  onBack: () => void;
  onUserUpdate: (user: User) => void;
  /** ID du tenant (owner) pour abonnement et infos entreprise */
  ownerId?: string;
  /** Callback pour ouvrir la gestion d'abonnement (Stripe) */
  onManageSubscription?: () => void;
  /** Message d'erreur Stripe à afficher sous le bouton Gérer l'abonnement */
  subscriptionError?: string | null;
  /** Callback pour importer des dossiers (Excel/CSV) — affiche le bouton Importer si défini */
  onImportJobs?: (jobsData: Array<Record<string, unknown>>) => Promise<void>;
  /** true = utilisateur est le propriétaire du compte (peut supprimer le compte) */
  isOwner?: boolean;
  /** Callback pour supprimer définitivement le compte (tout : partenaires, poseurs, jobs, stock, etc.) */
  onDeleteAccount?: () => Promise<{ error: string | null }>;
  /** Export complet des données (portabilité RGPD) — ZIP avec jobs, stock, logs, etc. */
  onExportAllData?: () => Promise<{ error: string | null }>;
}

// Fonction pour générer les initiales
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Fonction pour générer une couleur de fond basée sur le nom
function getAvatarColor(name: string): string {
  const colors = [
    "from-blue-500 to-blue-600",
    "from-purple-500 to-purple-600",
    "from-pink-500 to-pink-600",
    "from-green-500 to-green-600",
    "from-yellow-500 to-yellow-600",
    "from-red-500 to-red-600",
    "from-indigo-500 to-indigo-600",
    "from-teal-500 to-teal-600",
  ];
  
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

// Caractère de séparateur pour Excel (France) : point-virgule
const CSV_SEP = ";";
const CSV_EOL = "\r\n";

/** Échappe une cellule pour CSV (guillemets, sauts de ligne). */
function escapeCsvCell(value: string): string {
  const s = String(value)
    .replace(/\r\n|\r|\n/g, " ")
    .replace(/"/g, '""');
  return `"${s}"`;
}

// Fonction pour exporter les dossiers en CSV (compatible Excel FR)
function exportJobsToCSV(
  jobs: Job[],
  t: (key: string) => string
) {
  const headers = [
    t("settings.csvClient"),
    t("settings.csvPhone"),
    t("settings.csvAddress"),
    t("settings.csvStatus"),
    t("settings.csvDate"),
    t("settings.csvTime"),
    t("settings.csvSurface"),
    t("settings.csvLightingType"),
    t("settings.csvCeilingHeight"),
    t("settings.csvNotes"),
    t("settings.csvCreatedAt"),
  ];

  const rows = jobs.map((job) => [
    job.clientName,
    job.clientPhone,
    job.clientAddress,
    job.status === "pending" ? t("settings.statusPending") :
    job.status === "scheduled" ? t("settings.statusScheduled") :
    job.status === "in_progress" ? t("settings.statusInProgress") :
    job.status === "completed" ? t("settings.statusCompleted") : t("settings.statusCanceled"),
    job.date || "",
    job.time || "",
    job.surface || "",
    job.lightingType || "",
    job.ceilingHeight || "",
    job.notes || "",
    new Date(job.createdAt).toLocaleDateString("fr-FR"),
  ]);

  const csvContent = [
    headers.join(CSV_SEP),
    ...rows.map((row) =>
      row.map((cell) => escapeCsvCell(cell)).join(CSV_SEP)
    ),
  ].join(CSV_EOL);

  const blob = new Blob(["\ufeff" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `dossiers-${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Libellé du plan selon le statut d'abonnement (i18n)
function getPlanLabel(status: SubscriptionStatus | null, t: (key: string) => string): string {
  if (!status) return "—";
  const labels: Record<SubscriptionStatus, string> = {
    trialing: t("settings.planTrialing"),
    active: t("settings.planActive"),
    past_due: t("settings.planPastDue"),
    canceled: t("settings.planCanceled"),
    incomplete: t("settings.planIncomplete"),
  };
  return labels[status] ?? status;
}

// Cartes à hauteur uniforme en grille (flex + min-height) — définis hors du composant pour éviter remontée/focus à chaque frappe
const CARD_CLASS = "overflow-hidden rounded-2xl border border-gray-200/90 dark:border-white/[0.08] bg-white dark:bg-[#1c1c1e] shadow-sm flex flex-col min-h-[280px] lg:min-h-[320px]";
const Group = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="mb-6 lg:mb-8 flex flex-col h-full">
    <p className="mb-2 px-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">
      {label}
    </p>
    <div className={`${CARD_CLASS} flex-1`}>
      {children}
    </div>
  </div>
);
const Row = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`flex items-center justify-between gap-4 px-4 py-3.5 border-b border-gray-100 dark:border-white/[0.06] last:border-0 ${className}`}>
    {children}
  </div>
);
const GroupFull = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="mb-6 lg:mb-8 lg:col-span-2">
    <p className="mb-2 px-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">
      {label}
    </p>
    <div className="overflow-hidden rounded-2xl border border-gray-200/90 dark:border-white/[0.08] bg-white dark:bg-[#1c1c1e] shadow-sm">
      {children}
    </div>
  </div>
);

export function SettingsPage({ user, jobs, onBack, onUserUpdate, ownerId, onManageSubscription, subscriptionError, onImportJobs, isOwner, onDeleteAccount, onExportAllData }: SettingsPageProps) {
  const {
    profile,
    loading: profileLoading,
    uploadAvatar,
    removeAvatar,
    updatePreferences,
    changePassword,
    reload: reloadProfile,
  } = useProfile(user.id);

  const subscription = useSubscription(ownerId);
  const {
    profile: ownerProfile,
    updatePreferences: updateOwnerPreferences,
    loading: ownerProfileLoading,
  } = useProfile(ownerId);
  const { t, i18n } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // États pour le changement de mot de passe
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // États pour les notifications (alerte globale = on/off, plus d'email)
  const [globalAlerts, setGlobalAlerts] = useState(
    profile?.preferences?.emailNotifications ?? true
  );
  const [newJobsNotifications, setNewJobsNotifications] = useState(
    profile?.preferences?.newJobsNotifications ?? true
  );
  const [miniSecretaryEnabled, setMiniSecretaryEnabled] = useState(
    profile?.preferences?.miniSecretaryEnabled ?? true
  );

  // États pour l'upload d'avatar
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // États pour les infos entreprise (synchronisés depuis ownerProfile au chargement)
  const [companyForm, setCompanyForm] = useState({
    companyName: "",
    companySiret: "",
    companyAddress: "",
    companyPhone: "",
  });
  const [companySaving, setCompanySaving] = useState(false);
  const [companySuccess, setCompanySuccess] = useState(false);

  // Modal "Mes devis" / "Mes factures" (config template)
  const [invoiceTemplateModalOpen, setInvoiceTemplateModalOpen] = useState(false);
  const [invoiceTemplateModalMode, setInvoiceTemplateModalMode] = useState<"devis" | "facture">("devis");
  /** Préserver le scroll quand un clic provoque un re-render qui remonte en haut */
  const scrollRestoreRef = useRef(0);

  // Suppression du compte : modal + confirmation par saisie
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);

  const [exportAllLoading, setExportAllLoading] = useState(false);
  const [exportAllError, setExportAllError] = useState<string | null>(null);

  const [apiKeys, setApiKeys] = useState<{ id: string; name: string; created_at: string }[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [createKeyModalOpen, setCreateKeyModalOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createKeyLoading, setCreateKeyLoading] = useState(false);
  const [createKeyError, setCreateKeyError] = useState<string | null>(null);
  const [showKeyOnceModal, setShowKeyOnceModal] = useState(false);
  const [keyDisplay, setKeyDisplay] = useState("");
  const [apiKeysError, setApiKeysError] = useState<string | null>(null);

  const [webhooks, setWebhooks] = useState<{ id: string; url: string; events: string[]; active: boolean; created_at: string }[]>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvents, setWebhookEvents] = useState<string[]>([]);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookError, setWebhookError] = useState<string | null>(null);
  const [showWebhookSecretModal, setShowWebhookSecretModal] = useState(false);
  const [webhookSecretDisplay, setWebhookSecretDisplay] = useState("");
  const [webhooksError, setWebhooksError] = useState<string | null>(null);

  const [mfaFactors, setMfaFactors] = useState<{ totp?: { id: string }[] } | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaEnrollState, setMfaEnrollState] = useState<"idle" | "qr" | "verifying">("idle");
  const [mfaEnrollData, setMfaEnrollData] = useState<{ id: string; secret: string; qr_code?: string } | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  const initials = getInitials(user.name);
  const avatarColor = getAvatarColor(user.name);
  const currentAvatar = profile?.avatar_url || user.avatar_url;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.mfa.listFactors();
        if (!cancelled) setMfaFactors((data as { totp?: { id: string }[] }) ?? null);
      } catch {
        if (!cancelled) setMfaFactors(null);
      }
    })();
    return () => { cancelled = true; };
  }, [mfaEnrollState]);

  // Synchroniser les préférences notifications quand le profil charge
  useEffect(() => {
    if (profile?.preferences?.miniSecretaryEnabled !== undefined) {
      setMiniSecretaryEnabled(profile.preferences.miniSecretaryEnabled);
    }
    if (profile?.preferences?.emailNotifications !== undefined) {
      setGlobalAlerts(profile.preferences.emailNotifications);
    }
  }, [profile?.preferences?.miniSecretaryEnabled, profile?.preferences?.emailNotifications]);

  // Synchroniser le formulaire entreprise avec le profil owner
  useEffect(() => {
    if (ownerProfile?.preferences) {
      const p = ownerProfile.preferences;
      setCompanyForm({
        companyName: p.companyName ?? "",
        companySiret: p.companySiret ?? "",
        companyAddress: p.companyAddress ?? "",
        companyPhone: p.companyPhone ?? "",
      });
    }
  }, [ownerProfile?.preferences]);

  // Resynchroniser l'UI avec la langue sauvegardée (owner) dès qu'elle diffère
  useEffect(() => {
    if (!ownerId) return;
    const locale = ownerProfile?.preferences?.locale;
    if (locale !== "fr" && locale !== "en" && locale !== "es") return;
    if (getCurrentLocale() !== locale) i18n.changeLanguage(locale);
  }, [ownerId, ownerProfile?.preferences?.locale, i18n]);

  // Charger les clés API (owner uniquement, table api_keys doit exister)
  useEffect(() => {
    if (!ownerId || !isOwner) return;
    setApiKeysLoading(true);
    setApiKeysError(null);
    supabase
      .from("api_keys")
      .select("id, name, created_at")
      .order("created_at", { ascending: false })
      .then((res: { data: { id: string; name: string; created_at: string }[] | null; error: { message: string } | null }) => {
        setApiKeysLoading(false);
        if (res.error) {
          setApiKeysError(res.error.message);
          setApiKeys([]);
          return;
        }
        setApiKeys((res.data ?? []) as { id: string; name: string; created_at: string }[]);
      });
  }, [ownerId, isOwner]);

  // Charger les webhooks (owner uniquement)
  useEffect(() => {
    if (!ownerId || !isOwner) return;
    setWebhooksLoading(true);
    setWebhooksError(null);
    supabase
      .from("webhooks")
      .select("id, url, events, active, created_at")
      .order("created_at", { ascending: false })
      .then((res: { data: { id: string; url: string; events: string[]; active: boolean; created_at: string }[] | null; error: { message: string } | null }) => {
        setWebhooksLoading(false);
        if (res.error) {
          setWebhooksError(res.error.message);
          setWebhooks([]);
          return;
        }
        setWebhooks((res.data ?? []) as { id: string; url: string; events: string[]; active: boolean; created_at: string }[]);
      });
  }, [ownerId, isOwner]);

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateKeyError(null);
    setCreateKeyLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setCreateKeyError("Session expirée. Reconnectez-vous.");
        return;
      }
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-api-key`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ name: newKeyName.trim() || "API Key" }),
      });
      const json = await res.json();
      if (!res.ok) {
        setCreateKeyError(json.error ?? "Erreur lors de la création");
        return;
      }
      setKeyDisplay(json.key);
      setShowKeyOnceModal(true);
      setCreateKeyModalOpen(false);
      setNewKeyName("");
      setApiKeys((prev) => [{ id: json.id, name: json.name, created_at: json.created_at }, ...prev]);
    } finally {
      setCreateKeyLoading(false);
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    const { error } = await supabase.from("api_keys").delete().eq("id", id);
    if (!error) setApiKeys((prev) => prev.filter((k) => k.id !== id));
  };

  const WEBHOOK_EVENTS = [
    { id: "job.created", labelKey: "settings.jobCreated" },
    { id: "job.updated", labelKey: "settings.jobUpdated" },
    { id: "job.completed", labelKey: "settings.jobCompleted" },
  ] as const;

  const handleAddWebhook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId || !user?.id) return;
    setWebhookError(null);
    setWebhookSaving(true);
    const url = webhookUrl.trim();
    if (!url.startsWith("https://") && !url.startsWith("http://")) {
      setWebhookError("L'URL doit commencer par https:// ou http://");
      setWebhookSaving(false);
      return;
    }
    const secret = Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    const events = webhookEvents.length ? webhookEvents : WEBHOOK_EVENTS.map((e) => e.id);
    const { data, error } = await supabase
      .from("webhooks")
      .insert({ owner_id: ownerId, url, secret, events, active: true })
      .select("id, url, events, active, created_at")
      .single();
    setWebhookSaving(false);
    if (error) {
      setWebhookError(error.message);
      return;
    }
    setWebhookSecretDisplay(secret);
    setShowWebhookSecretModal(true);
    setWebhookModalOpen(false);
    setWebhookUrl("");
    setWebhookEvents([]);
    setWebhooks((prev) => [{ id: data.id, url: data.url, events: data.events ?? [], active: data.active, created_at: data.created_at }, ...prev]);
  };

  const handleToggleWebhookActive = async (id: string, active: boolean) => {
    const { error } = await supabase.from("webhooks").update({ active }).eq("id", id);
    if (!error) setWebhooks((prev) => prev.map((w) => (w.id === id ? { ...w, active } : w)));
  };

  const handleDeleteWebhook = async (id: string) => {
    const { error } = await supabase.from("webhooks").delete().eq("id", id);
    if (!error) setWebhooks((prev) => prev.filter((w) => w.id !== id));
  };

  // Sauvegarder les infos entreprise (profil owner)
  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerId) return;
    setCompanySaving(true);
    setCompanySuccess(false);
    const prefs = {
      ...ownerProfile?.preferences,
      companyName: companyForm.companyName || undefined,
      companySiret: companyForm.companySiret || undefined,
      companyAddress: companyForm.companyAddress || undefined,
      companyPhone: companyForm.companyPhone || undefined,
    };
    const { error } = await updateOwnerPreferences(prefs);
    setCompanySaving(false);
    if (!error) {
      setCompanySuccess(true);
      setTimeout(() => setCompanySuccess(false), 3000);
    }
  };

  // Restaurer le scroll après tout clic qui provoque une remontée en haut (toggles, boutons, etc.)
  useLayoutEffect(() => {
    const saved = scrollRestoreRef.current;
    if (saved > 0 && window.scrollY < saved - 50) {
      window.scrollTo({ top: saved, behavior: "auto" });
    }
    scrollRestoreRef.current = 0;
  });

  // Gestion de l'upload d'avatar
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setUploadError(null);

    const { url, error } = await uploadAvatar(file);

    if (error) {
      setUploadError(error);
    } else if (url) {
      // Mettre à jour l'utilisateur avec la nouvelle URL
      onUserUpdate({ ...user, avatar_url: url });
    }

    setUploadLoading(false);
    
    // Réinitialiser l'input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Gestion de la suppression d'avatar
  const handleRemoveAvatar = async () => {
    setUploadLoading(true);
    setUploadError(null);

    const { error } = await removeAvatar();

    if (error) {
      setUploadError(error);
    } else {
      onUserUpdate({ ...user, avatar_url: null });
    }

    setUploadLoading(false);
  };

  const totpFactors = mfaFactors?.totp ?? [];
  const hasMfa = totpFactors.length > 0;

  const handleMfaEnrollStart = async () => {
    setMfaError(null);
    setMfaLoading(true);
    try {
      const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "Authenticator",
      });
      if (enrollErr) throw enrollErr;
      const d = data as { id: string; secret: string; qr_code?: string };
      setMfaEnrollData({ id: d.id, secret: d.secret, qr_code: d.qr_code });
      setMfaEnrollState("qr");
      setMfaCode("");
    } catch (e) {
      setMfaError(e instanceof Error ? e.message : "Erreur activation 2FA");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaEnrollVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaEnrollData || !mfaCode.trim() || mfaCode.trim().length < 6) {
      setMfaError("Entrez le code à 6 chiffres");
      return;
    }
    setMfaError(null);
    setMfaLoading(true);
    try {
      const { data: challengeData, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: mfaEnrollData.id,
      });
      if (chErr) throw chErr;
      const challengeId = (challengeData as { id: string })?.id;
      if (!challengeId) throw new Error("Échec du défi");
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: mfaEnrollData.id,
        challengeId,
        code: mfaCode.trim(),
      });
      if (vErr) throw vErr;
      setMfaEnrollState("idle");
      setMfaEnrollData(null);
      setMfaCode("");
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      setMfaFactors((factorsData as { totp?: { id: string }[] }) ?? null);
    } catch (e) {
      setMfaError(e instanceof Error ? e.message : "Code invalide");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaUnenroll = async () => {
    const factorId = totpFactors[0]?.id;
    if (!factorId) return;
    setMfaError(null);
    setMfaLoading(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      setMfaFactors(null);
    } catch (e) {
      setMfaError(e instanceof Error ? e.message : "Erreur désactivation 2FA");
    } finally {
      setMfaLoading(false);
    }
  };

  // Gestion du changement de mot de passe
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
      setPasswordError("Le mot de passe doit faire au moins 6 caractères");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("Les mots de passe ne correspondent pas");
      return;
    }

    setPasswordLoading(true);

    const { error } = await changePassword(passwordForm.newPassword);

    if (error) {
      setPasswordError(error);
    } else {
      setPasswordSuccess(true);
      setPasswordForm({ newPassword: "", confirmPassword: "" });
      
      // Masquer le message de succès après 3 secondes
      setTimeout(() => setPasswordSuccess(false), 3000);
    }

    setPasswordLoading(false);
  };

  // Gestion des préférences de notifications (alerte globale stockée dans emailNotifications pour rétrocompat)
  const handleNotificationToggle = async (type: "global" | "newJobs" | "miniSecretary", value: boolean) => {
    const newPreferences = {
      ...profile?.preferences,
      emailNotifications: type === "global" ? value : globalAlerts,
      newJobsNotifications: type === "newJobs" ? value : newJobsNotifications,
      miniSecretaryEnabled: type === "miniSecretary" ? value : miniSecretaryEnabled,
    };

    if (type === "global") setGlobalAlerts(value);
    else if (type === "newJobs") setNewJobsNotifications(value);
    else if (type === "miniSecretary") setMiniSecretaryEnabled(value);

    await updatePreferences(newPreferences);
  };

  // Export CSV
  const handleExportCSV = () => {
    exportJobsToCSV(jobs, t);
  };

  const statusLabelToValue: Record<string, "pending" | "scheduled" | "in_progress" | "completed" | "cancelled"> = {
    "en attente": "pending",
    "planifié": "scheduled",
    "en cours": "in_progress",
    "terminé": "completed",
    "annulé": "cancelled",
  };
  const parseImportRow = (row: Record<string, string>): Record<string, unknown> => {
    const get = (key: string) => (row[key] ?? "").trim();
    const stat = (get("Statut") || get("statut")).toLowerCase();
    return {
      clientName: get("Client") || get("client"),
      clientPhone: get("Téléphone") || get("telephone") || get("téléphone"),
      clientAddress: get("Adresse") || get("adresse"),
      status: statusLabelToValue[stat] ?? "pending",
      date: get("Date") || get("date") || undefined,
      time: get("Heure") || get("heure") || undefined,
      surface: get("Surface") || get("surface") || undefined,
      lightingType: get("Type d'éclairage") || get("type d'éclairage") || get("lightingType") || undefined,
      ceilingHeight: get("Hauteur plafond") || get("hauteur plafond") || get("ceilingHeight") || undefined,
      notes: get("Notes") || get("notes") || undefined,
    };
  };
  function parseCSVLine(line: string): string[] {
    const out: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQuotes = !inQuotes;
      } else if (((c === "," || c === ";") && !inQuotes) || (c === "\n" && !inQuotes)) {
        out.push(cur.trim());
        cur = "";
        if (c === "\n") break;
      } else cur += c;
    }
    out.push(cur.trim());
    return out;
  }
  const parseFileToJobs = (file: File): Promise<Array<Record<string, unknown>>> => {
    return new Promise((resolve, reject) => {
      const isCsv = /\.csv$/i.test(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = (e.target?.result as string) || "";
          if (isCsv) {
            const lines = text.split(/\r?\n/).filter((l) => l.trim());
            if (lines.length < 2) {
              resolve([]);
              return;
            }
            const headers = parseCSVLine(lines[0]);
            const rows: Array<Record<string, unknown>> = [];
            for (let i = 1; i < lines.length; i++) {
              const cells = parseCSVLine(lines[i]);
              const row: Record<string, string> = {};
              headers.forEach((h, idx) => {
                row[h] = cells[idx] ?? "";
              });
              rows.push(parseImportRow(row));
            }
            resolve(rows);
          } else {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const wb = XLSX.read(data, { type: "array" });
            const firstSheet = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json<Record<string, string>>(firstSheet, { defval: "" });
            resolve(json.map((row) => parseImportRow(row)));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
      if (isCsv) reader.readAsText(file, "UTF-8");
      else reader.readAsArrayBuffer(file);
    });
  };
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onImportJobs) return;
    setImportError(null);
    setImportLoading(true);
    try {
      const rows = await parseFileToJobs(file);
      const valid = rows.filter((r) => (String(r.clientName ?? "").trim() || String(r.clientAddress ?? "").trim()));
      if (valid.length === 0) {
        setImportError("Aucune ligne valide (Client ou Adresse requis).");
        return;
      }
      await onImportJobs(valid);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Erreur lors de l'import.");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <>
    <div
      className="min-h-screen bg-[#f2f2f7] dark:bg-[#09090b] pb-20 w-full"
      onClickCapture={() => { scrollRestoreRef.current = window.scrollY; }}
    >
      <div className="mx-auto max-w-5xl px-4 pt-6 pb-6 sm:px-6">
        {/* En-tête */}
        <header className="mb-8">
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 mb-4 p-2 rounded-full text-gray-500 dark:text-slate-400 hover:bg-black/5 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
            aria-label={t("settings.back")}
          >
            <ArrowLeft size={24} strokeWidth={1.8} />
          </button>
          <h1 className="text-[34px] font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
            {t("settings.title")}
          </h1>
          <p className="mt-1 text-[17px] text-gray-500 dark:text-slate-400">
            {t("settings.subtitle")}
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
        {/* PROFIL */}
        <Group label={t("settings.sectionProfile")}>
          <div className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500 mb-3">
              {t("settings.profilePhoto")}
            </p>
            <div className="flex flex-col items-center text-center">
              <div className="relative w-24 h-24 rounded-full overflow-hidden ring-2 ring-gray-200/80 dark:ring-white/10 flex-shrink-0">
                {currentAvatar ? (
                  <img src={currentAvatar} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white font-semibold text-2xl`}>
                    {initials}
                  </div>
                )}
                {uploadLoading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 size={28} className="text-white animate-spin" strokeWidth={2} />
                  </div>
                )}
              </div>
              {uploadError && <p className="mt-2 text-[13px] text-red-500 dark:text-red-400">{uploadError}</p>}
              <p className="mt-1.5 text-[13px] text-gray-400 dark:text-slate-500">{t("settings.profilePhotoHint")}</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" disabled={uploadLoading} />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadLoading}
                className="flex-1 min-w-[120px] h-11 flex items-center justify-center gap-2 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[15px] font-semibold transition-colors active:scale-[0.99] disabled:opacity-50"
              >
                <Upload size={18} strokeWidth={2} />
                {t("settings.change")}
              </button>
              {currentAvatar && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  disabled={uploadLoading}
                  className="flex-1 min-w-[120px] h-11 flex items-center justify-center gap-2 rounded-xl text-[15px] font-medium text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  <Trash2 size={18} strokeWidth={2} />
                  {t("settings.remove")}
                </button>
              )}
            </div>
          </div>
          {ownerId != null && (
            <>
              <div className="h-px bg-gray-100 dark:bg-white/[0.06]" />
              <div className="p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500 mb-2">
                  {t("settings.language")}
                </p>
                <p className="text-[13px] text-gray-500 dark:text-slate-400 mb-3">{t("settings.languageDesc")}</p>
                <div className="flex gap-2">
                  {(["fr", "en", "es"] as const).map((locale) => (
                    <button
                      key={locale}
                      type="button"
                      onClick={async () => {
                        i18n.changeLanguage(locale);
                        await updateOwnerPreferences({ ...ownerProfile?.preferences, locale });
                      }}
                      className={`flex-1 py-2.5 rounded-lg text-[15px] font-medium transition-all duration-200 ${
                        getCurrentLocale() === locale
                          ? "bg-[#007AFF] text-white"
                          : "bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/15"
                      }`}
                    >
                      {locale === "fr" ? t("common.langFr") : locale === "en" ? t("common.langEn") : t("common.langEs")}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </Group>

        {/* SÉCURITÉ — mot de passe + 2FA */}
        <Group label={t("settings.sectionSecurity")}>
          <div className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500 mb-3">
              {t("settings.password")}
            </p>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 mb-1.5">{t("settings.newPassword")}</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                className="w-full h-11 px-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] transition-all"
                placeholder="••••••••"
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 mb-1.5">{t("settings.confirmPassword")}</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                className="w-full h-11 px-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] transition-all"
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>
              {passwordError && (
                <div className="flex items-center gap-2 py-2.5 px-3 rounded-xl bg-red-50 dark:bg-red-500/10 text-[13px] text-red-600 dark:text-red-400">
                  <X size={16} strokeWidth={2.2} />
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="flex items-center gap-2 py-2.5 px-3 rounded-xl bg-green-50 dark:bg-green-500/10 text-[13px] text-green-700 dark:text-green-400">
                  <Check size={16} strokeWidth={2.2} />
                  {t("settings.passwordUpdated")}
                </div>
              )}
              <button
                type="submit"
                disabled={passwordLoading}
                className="w-full h-11 flex items-center justify-center gap-2 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[15px] font-semibold transition-colors active:scale-[0.99] disabled:opacity-50"
              >
                {passwordLoading ? <Loader2 size={18} className="animate-spin" strokeWidth={2} /> : <Lock size={18} strokeWidth={2} />}
                {passwordLoading ? t("settings.changing") : t("settings.changePasswordBtn")}
              </button>
            </form>
          </div>
          <div className="h-px bg-gray-100 dark:bg-white/[0.06]" />
          <div className="p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500 mb-3">
              {t("settings.security2FADesc")}
            </p>
            {mfaError && <p className="text-[13px] text-red-600 dark:text-red-400 mb-3">{mfaError}</p>}
            {mfaEnrollState === "qr" && mfaEnrollData && (
              <div className="mx-auto max-w-sm">
                <div className="rounded-2xl bg-gray-50/80 dark:bg-white/[0.04] border border-gray-100 dark:border-white/[0.08] p-6 text-center">
                  <p className="text-[15px] text-gray-700 dark:text-slate-300 leading-relaxed mb-5">
                    {t("settings.scanQR")}
                  </p>
                  {mfaEnrollData.qr_code && (
                    <div className="inline-flex items-center justify-center p-4 rounded-2xl bg-white dark:bg-white/10 border border-gray-200/80 dark:border-white/10 shadow-sm mb-5">
                      <img src={mfaEnrollData.qr_code} alt="QR Code" className="w-44 h-44 rounded-xl" />
                    </div>
                  )}
                  <p className="text-[12px] text-gray-500 dark:text-slate-400 mb-1">Ou saisissez ce code dans votre app :</p>
                  <p className="text-[11px] font-mono text-gray-400 dark:text-slate-500 break-all px-2 mb-5">
                    {mfaEnrollData.secret}
                  </p>
                  <form onSubmit={handleMfaEnrollVerify} className="space-y-4">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                      placeholder={t("settings.enterCode6")}
                      className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white text-center text-[20px] tracking-[0.4em] font-medium focus:outline-none focus:ring-2 focus:ring-[#007AFF]/40 focus:border-[#007AFF] transition-all"
                    />
                    <div className="flex gap-3 justify-center">
                      <button type="button" onClick={() => { setMfaEnrollState("idle"); setMfaEnrollData(null); setMfaCode(""); setMfaError(null); }} className="h-11 px-6 rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 text-[15px] font-semibold hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                        {t("settings.cancel")}
                      </button>
                      <button type="submit" disabled={mfaLoading || mfaCode.length < 6} className="h-11 px-6 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[15px] font-semibold disabled:opacity-50 transition-colors min-w-[120px]">
                        {mfaLoading ? t("settings.verifying") : t("settings.validate")}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            {mfaEnrollState === "idle" && (
              <div className="flex flex-wrap justify-center gap-2">
                {hasMfa ? (
                  <button type="button" onClick={handleMfaUnenroll} disabled={mfaLoading} className="h-11 px-5 rounded-xl border border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 text-[15px] font-semibold disabled:opacity-50">
                    {mfaLoading ? "…" : t("settings.disable2FA")}
                  </button>
                ) : (
                  <button type="button" onClick={handleMfaEnrollStart} disabled={mfaLoading} className="h-11 px-5 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[15px] font-semibold disabled:opacity-50">
                    {mfaLoading ? t("settings.preparing") : t("settings.enable2FA")}
                  </button>
                )}
              </div>
            )}
          </div>
        </Group>

        {/* Devis & Factures — juste sous Profile et Sécurité */}
        {ownerId != null && (
          <>
            <GroupFull label={t("settings.invoiceTemplateSection")}>
              <p className="px-5 pt-4 pb-4 text-[13px] text-gray-500 dark:text-slate-400">
                {t("settings.invoiceTemplateDesc")}
              </p>
              <div className="px-5 pb-5 flex gap-2 p-1 rounded-2xl bg-gray-100/80 dark:bg-white/[0.06] border border-gray-200/50 dark:border-white/[0.06]">
                <button
                  type="button"
                  onClick={() => { setInvoiceTemplateModalMode("devis"); setInvoiceTemplateModalOpen(true); }}
                  className="flex-1 h-12 rounded-xl font-medium text-[14px] flex items-center justify-center gap-2.5 bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm border border-gray-200/60 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/15 hover:shadow transition-all duration-200 active:scale-[0.99]"
                >
                  <FileText size={20} strokeWidth={2} className="text-[#007AFF] dark:text-[#0A84FF]" />
                  {t("settings.myQuotes")}
                </button>
                <button
                  type="button"
                  onClick={() => { setInvoiceTemplateModalMode("facture"); setInvoiceTemplateModalOpen(true); }}
                  className="flex-1 h-12 rounded-xl font-medium text-[14px] flex items-center justify-center gap-2.5 bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm border border-gray-200/60 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/15 hover:shadow transition-all duration-200 active:scale-[0.99]"
                >
                  <FileText size={20} strokeWidth={2} className="text-emerald-600 dark:text-emerald-400" />
                  {t("settings.myInvoices")}
                </button>
              </div>
            </GroupFull>
            <InvoiceTemplateModal
              isOpen={invoiceTemplateModalOpen}
              onClose={() => setInvoiceTemplateModalOpen(false)}
              mode={invoiceTemplateModalMode}
              ownerId={ownerId}
              ownerProfile={ownerProfile ?? undefined}
              ownerProfileLoading={ownerProfileLoading}
              updateOwnerPreferences={updateOwnerPreferences}
              companyName={companyForm.companyName}
            />
          </>
        )}

        {/* INTÉGRATIONS (owner) — pleine largeur */}
        {ownerId != null && isOwner && (
          <GroupFull label={t("settings.sectionIntegrations")}>
            <div className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500 mb-3">
                {t("settings.apiKeys")}
              </p>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/15 dark:bg-amber-500/20 flex items-center justify-center shrink-0">
                    <Key size={18} className="text-amber-600 dark:text-amber-400" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] text-gray-500 dark:text-slate-400">{t("settings.apiKeysDesc")}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setCreateKeyModalOpen(true); setCreateKeyError(null); setNewKeyName(""); }}
                  disabled={apiKeysLoading}
                  className="h-11 px-4 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[14px] font-semibold transition-colors disabled:opacity-50 flex items-center gap-2 shrink-0"
                >
                  <Key size={16} />
                  {t("settings.createKey")}
                </button>
              </div>
              {apiKeysError && <p className="text-[13px] text-amber-600 dark:text-amber-400 mb-2">{apiKeysError}</p>}
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {apiKeysLoading ? (
                  <div className="flex justify-center py-6"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
                ) : (
                  apiKeys.map((k) => (
                    <div key={k.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5">
                      <div className="min-w-0">
                        <p className="text-[15px] font-medium text-gray-900 dark:text-white truncate">{k.name}</p>
                        <p className="text-[12px] text-gray-500 dark:text-slate-400">{new Date(k.created_at).toLocaleDateString()}</p>
                      </div>
                      <button type="button" onClick={() => handleDeleteApiKey(k.id)} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors" title={t("settings.deleteKey")}>
                        <Trash2 size={18} strokeWidth={2} />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <a href="/openapi.yaml" target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[14px] text-[#007AFF] hover:underline">
                <ExternalLink size={16} />
                {t("settings.docApi")}
              </a>
            </div>
            <div className="h-px bg-gray-100 dark:bg-white/[0.06]" />
            <div className="p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500 mb-3">
                {t("settings.webhooks")}
              </p>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-sky-500/15 dark:bg-sky-500/20 flex items-center justify-center shrink-0">
                    <Link2 size={18} className="text-sky-600 dark:text-sky-400" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] text-gray-500 dark:text-slate-400">{t("settings.webhooksDesc")}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setWebhookModalOpen(true); setWebhookError(null); setWebhookUrl(""); setWebhookEvents([]); }}
                  disabled={webhooksLoading}
                  className="h-11 px-4 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[14px] font-semibold transition-colors disabled:opacity-50 flex items-center gap-2 shrink-0"
                >
                  <Link2 size={16} />
                  {t("settings.addWebhook")}
                </button>
              </div>
              {webhooksError && <p className="text-[13px] text-amber-600 dark:text-amber-400 mb-2">{webhooksError}</p>}
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {webhooksLoading ? (
                  <div className="flex justify-center py-6"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
                ) : (
                  webhooks.map((w) => (
                    <div key={w.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5">
                      <div className="min-w-0 flex-1">
                        <p className="text-[15px] font-medium text-gray-900 dark:text-white truncate">{w.url}</p>
                        <p className="text-[12px] text-gray-500 dark:text-slate-400">{w.events.join(", ")} · {w.active ? t("settings.active") : t("settings.inactive")}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button type="button" onClick={() => handleToggleWebhookActive(w.id, !w.active)} className={`px-2.5 py-1 rounded-lg text-[12px] font-medium ${w.active ? "bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400" : "bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-slate-400"}`}>
                          {w.active ? t("settings.disable") : t("settings.enable")}
                        </button>
                        <button type="button" onClick={() => handleDeleteWebhook(w.id)} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10" title={t("access.delete")}>
                          <Trash2 size={18} strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </GroupFull>
        )}

        {/* ENTREPRISE — pleine largeur */}
        {ownerId != null && (
          <GroupFull label={t("settings.sectionCompany")}>
            <form onSubmit={handleSaveCompany} className="p-4 space-y-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500 mb-1">
                {t("settings.companyInfoDesc")}
              </p>
              {[
                { key: "companyName" as const, label: t("settings.companyNameLabel"), placeholder: "Ma Société SARL" },
                { key: "companySiret" as const, label: t("settings.companySiret"), placeholder: "123 456 789 00012" },
                { key: "companyAddress" as const, label: t("settings.companyAddressLabel"), placeholder: "123 rue Example, 75001 Paris" },
                { key: "companyPhone" as const, label: t("settings.companyPhoneLabel"), placeholder: "01 23 45 67 89" },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-[13px] font-medium text-gray-500 dark:text-slate-400 mb-1.5">{label}</label>
                  <input
                    type={key === "companyPhone" ? "tel" : "text"}
                    value={companyForm[key]}
                    onChange={(e) => setCompanyForm({ ...companyForm, [key]: e.target.value })}
                    className="w-full h-11 px-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] transition-all"
                    placeholder={placeholder}
                  />
                </div>
              ))}
              {companySuccess && (
                <div className="flex items-center gap-2 py-2.5 px-3 rounded-xl bg-green-50 dark:bg-green-500/10 text-[13px] text-green-700 dark:text-green-400">
                  <Check size={16} strokeWidth={2.2} />
                  {t("settings.saved")}
                </div>
              )}
              <button
                type="submit"
                disabled={companySaving || ownerProfileLoading}
                className="w-full h-11 flex items-center justify-center gap-2 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[15px] font-semibold transition-colors active:scale-[0.99] disabled:opacity-50"
              >
                {companySaving ? <Loader2 size={18} className="animate-spin" strokeWidth={2} /> : <Save size={18} strokeWidth={2} />}
                {companySaving ? t("settings.saving") : t("settings.saveBtn")}
              </button>
            </form>
          </GroupFull>
        )}

        {/* DONNÉES */}
        <Group label={t("settings.sectionData")}>
          <input ref={importFileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="hidden" />
          <p className="px-4 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">
            {t("settings.dataDesc")}
          </p>
          <div className="divide-y divide-gray-100 dark:divide-white/[0.06]">
            {onExportAllData && (
              <Row>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-white/10 flex items-center justify-center shrink-0">
                    {exportAllLoading ? <Loader2 size={20} className="text-gray-500 dark:text-slate-400 animate-spin" strokeWidth={2} /> : <Download size={20} className="text-gray-600 dark:text-slate-300" strokeWidth={2} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-gray-900 dark:text-white">{t("settings.exportData")}</p>
                    <p className="text-[13px] text-gray-500 dark:text-slate-400">{t("settings.exportDataDesc")}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async () => { setExportAllError(null); setExportAllLoading(true); try { const { error } = await onExportAllData(); if (error) setExportAllError(error); } finally { setExportAllLoading(false); } }}
                  disabled={exportAllLoading}
                  className="h-11 px-4 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[14px] font-semibold transition-colors shrink-0 disabled:opacity-50"
                >
                  {exportAllLoading ? t("settings.generating") : t("settings.downloadAll")}
                </button>
              </Row>
            )}
            {exportAllError && (
              <div className="px-4 py-3">
                <p className="text-[13px] text-red-600 dark:text-red-400">{exportAllError}</p>
              </div>
            )}
            <Row>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-white/10 flex items-center justify-center shrink-0">
                  <Download size={20} className="text-gray-600 dark:text-slate-300" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-gray-900 dark:text-white">{t("settings.exportJobs")}</p>
                  <p className="text-[13px] text-gray-500 dark:text-slate-400">CSV Excel · {jobs.length} dossier{jobs.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <button type="button" onClick={handleExportCSV} className="h-11 px-4 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[14px] font-semibold transition-colors shrink-0">
                {t("settings.download")}
              </button>
            </Row>
            {onImportJobs && (
              <Row>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-white/10 flex items-center justify-center shrink-0">
                    {importLoading ? <Loader2 size={20} className="text-gray-500 dark:text-slate-400 animate-spin" strokeWidth={2} /> : <Upload size={20} className="text-gray-600 dark:text-slate-300" strokeWidth={2} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-gray-900 dark:text-white">{t("settings.importJobs")}</p>
                    <p className="text-[13px] text-gray-500 dark:text-slate-400">{t("settings.importJobsHint")}</p>
                  </div>
                </div>
                <button type="button" onClick={() => importFileInputRef.current?.click()} disabled={importLoading} className="h-11 px-4 rounded-xl border border-gray-300 dark:border-white/20 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/10 text-[14px] font-semibold transition-colors shrink-0 disabled:opacity-50">
                  {importLoading ? "Import…" : t("settings.chooseFile")}
                </button>
              </Row>
            )}
          </div>
          {importError && (
            <div className="px-4 py-3 flex items-center gap-2 text-[13px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10">
              <X size={16} strokeWidth={2} />
              {importError}
            </div>
          )}
        </Group>

        {/* NOTIFICATIONS */}
        <Group label={t("settings.sectionNotifications")}>
          <p className="px-4 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">
            {t("settings.notificationsDesc")}
          </p>
          <Row>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-gray-900 dark:text-white">{t("settings.globalAlert")}</p>
              <p className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">{t("settings.globalAlertDesc")}</p>
            </div>
            <button type="button" onClick={() => handleNotificationToggle("global", !globalAlerts)} className={`relative inline-flex h-8 w-14 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${globalAlerts ? "bg-[#34C759]" : "bg-gray-300 dark:bg-white/20"}`}>
              <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ml-1 ${globalAlerts ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </Row>
          <Row className={!globalAlerts ? "opacity-60" : ""}>
            <div className="min-w-0">
              <p className={`text-[15px] font-semibold ${globalAlerts ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-slate-500"}`}>{t("settings.newJobsNotif")}</p>
              <p className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">{t("settings.newJobsNotifDesc")}</p>
            </div>
            <button type="button" disabled={!globalAlerts} onClick={() => globalAlerts && handleNotificationToggle("newJobs", !newJobsNotifications)} className={`relative inline-flex h-8 w-14 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${!globalAlerts ? "bg-gray-300 dark:bg-white/10 cursor-not-allowed" : newJobsNotifications ? "bg-[#34C759]" : "bg-gray-300 dark:bg-white/20"}`}>
              <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ml-1 ${newJobsNotifications ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </Row>
          <Row className={!globalAlerts ? "opacity-60" : ""}>
            <div className="min-w-0">
              <p className={`text-[15px] font-semibold ${globalAlerts ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-slate-500"}`}>{t("settings.miniSecretary")}</p>
              <p className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">{t("settings.miniSecretaryDesc")}</p>
            </div>
            <button type="button" disabled={!globalAlerts} onClick={() => globalAlerts && handleNotificationToggle("miniSecretary", !miniSecretaryEnabled)} className={`relative inline-flex h-8 w-14 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${!globalAlerts ? "bg-gray-300 dark:bg-white/10 cursor-not-allowed" : miniSecretaryEnabled ? "bg-[#34C759]" : "bg-gray-300 dark:bg-white/20"}`}>
              <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ml-1 ${miniSecretaryEnabled ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </Row>
        </Group>

        {/* ABONNEMENT — pleine largeur, carte type CB */}
        {ownerId != null && (
          <GroupFull label={t("settings.sectionSubscription")}>
            <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-white/10 dark:to-white/5 border border-gray-200/80 dark:border-white/10 flex items-center justify-center shrink-0 shadow-inner">
                <CreditCard size={28} className="text-gray-600 dark:text-slate-400" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <p className="text-[15px] font-semibold text-gray-900 dark:text-white">
                  {subscription.loading ? "…" : getPlanLabel(subscription.status, t)}
                </p>
                <p className="text-[13px] text-gray-500 dark:text-slate-400 mt-0.5">
                  {t("settings.currentStatus")} · {t("settings.billingPlan")}
                </p>
              </div>
              {onManageSubscription && (
                <button
                  type="button"
                  onClick={onManageSubscription}
                  className="h-11 px-6 rounded-xl bg-[#007AFF] hover:bg-[#0051D5] text-white text-[15px] font-semibold transition-colors active:scale-[0.99] flex items-center gap-2 shrink-0"
                >
                  <CreditCard size={18} strokeWidth={2} />
                  {t("settings.manageSubscription")}
                </button>
              )}
            </div>
            {subscriptionError && <p className="text-[13px] text-red-500 dark:text-red-400 px-5 sm:px-6 pb-4">{subscriptionError}</p>}
          </GroupFull>
        )}

        {/* SUPPRIMER MON COMPTE — pleine largeur */}
        {isOwner && onDeleteAccount && (
          <GroupFull label={t("settings.sectionDanger")}>
            <div className="p-4">
              <p className="text-[15px] text-gray-600 dark:text-slate-300 mb-4">{t("settings.deleteAccountWarn")}</p>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => { setDeleteAccountModalOpen(true); setDeleteConfirmInput(""); setDeleteAccountError(null); }}
                  className="h-11 px-5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[15px] font-semibold transition-colors active:scale-[0.99]"
                >
                  {t("settings.deleteAccountBtn")}
                </button>
              </div>
            </div>
          </GroupFull>
        )}
        </div>
      </div>
    </div>

      {/* Modal de confirmation suppression compte */}
      {deleteAccountModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70" onClick={() => !deleteAccountLoading && setDeleteAccountModalOpen(false)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("settings.deleteAccountModalTitle")}</h3>
            <p className="text-sm text-gray-600 dark:text-slate-300 mb-4">
              {t("settings.deleteAccountModalDesc")}
            </p>
            <input
              type="text"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              placeholder={t("settings.deleteConfirmPlaceholder")}
              className="w-full h-12 px-3 rounded-xl bg-gray-50 dark:bg-white/[0.06] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 mb-4"
              disabled={deleteAccountLoading}
            />
            {deleteAccountError && (
              <p className="text-sm text-red-500 dark:text-red-400 mb-4 flex items-center gap-2">
                <X size={16} />
                {deleteAccountError}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => !deleteAccountLoading && setDeleteAccountModalOpen(false)}
                disabled={deleteAccountLoading}
                className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/15 disabled:opacity-50"
              >
                {t("settings.cancel")}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (deleteConfirmInput.trim() !== t("settings.deleteConfirmPlaceholder")) return;
                  setDeleteAccountLoading(true);
                  setDeleteAccountError(null);
                  const { error } = await onDeleteAccount!();
                  setDeleteAccountLoading(false);
                  if (error) {
                    setDeleteAccountError(error);
                  } else {
                    setDeleteAccountModalOpen(false);
                  }
                }}
                disabled={deleteConfirmInput.trim() !== t("settings.deleteConfirmPlaceholder") || deleteAccountLoading}
                className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleteAccountLoading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {deleteAccountLoading ? t("settings.deleting") : t("settings.deletePermanently")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal créer clé API */}
      {createKeyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70" onClick={() => !createKeyLoading && setCreateKeyModalOpen(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("settings.newApiKey")}</h3>
            <form onSubmit={handleCreateApiKey} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">{t("settings.keyNameOptional")}</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder={t("settings.keyNamePlaceholder")}
                  className="w-full h-12 px-3 rounded-xl bg-gray-50 dark:bg-white/[0.06] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  disabled={createKeyLoading}
                />
              </div>
              {createKeyError && (
                <p className="text-sm text-red-500 dark:text-red-400 flex items-center gap-2">
                  <X size={16} />
                  {createKeyError}
                </p>
              )}
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => !createKeyLoading && setCreateKeyModalOpen(false)} disabled={createKeyLoading} className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/15 disabled:opacity-50">
                  {t("settings.cancel")}
                </button>
                <button type="submit" disabled={createKeyLoading} className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
                  {createKeyLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {t("settings.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal afficher la clé une seule fois */}
      {showKeyOnceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70" onClick={() => setShowKeyOnceModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("settings.keyCreated")}</h3>
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">{t("settings.keySaveNotice")}</p>
            <div className="flex items-center gap-2 mb-4">
              <code className="flex-1 px-3 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white text-sm font-mono break-all">
                {keyDisplay}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(keyDisplay)}
                className="p-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                title={t("settings.copy")}
              >
                <Copy size={18} strokeWidth={2} />
              </button>
            </div>
            <button type="button" onClick={() => setShowKeyOnceModal(false)} className="w-full py-2.5 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/15">
              {t("settings.close")}
            </button>
          </div>
        </div>
      )}

      {/* Modal ajouter webhook */}
      {webhookModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70" onClick={() => !webhookSaving && setWebhookModalOpen(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("settings.newWebhook")}</h3>
            <form onSubmit={handleAddWebhook} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 dark:text-slate-400 mb-1">{t("settings.webhookUrlLabel")}</label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder={t("settings.webhookUrlPlaceholder")}
                  className="w-full h-12 px-3 rounded-xl bg-gray-50 dark:bg-white/[0.06] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30"
                  required
                  disabled={webhookSaving}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 dark:text-slate-400 mb-2">{t("settings.webhookEventsLabel")}</label>
                <div className="space-y-2">
                  {WEBHOOK_EVENTS.map((ev) => (
                    <label key={ev.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={webhookEvents.includes(ev.id)}
                        onChange={(e) => setWebhookEvents((prev) => (e.target.checked ? [...prev, ev.id] : prev.filter((x) => x !== ev.id)))}
                        className="rounded border-gray-300 dark:border-slate-500 text-sky-500 focus:ring-sky-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-slate-300">{t(ev.labelKey)}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{t("settings.webhookEventsHint")}</p>
              </div>
              {webhookError && (
                <p className="text-sm text-red-500 dark:text-red-400 flex items-center gap-2">
                  <X size={16} />
                  {webhookError}
                </p>
              )}
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => !webhookSaving && setWebhookModalOpen(false)} disabled={webhookSaving} className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/15 disabled:opacity-50">
                  {t("settings.cancel")}
                </button>
                <button type="submit" disabled={webhookSaving} className="px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2">
                  {webhookSaving ? <Loader2 size={16} className="animate-spin" /> : null}
                  {t("access.add")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal secret webhook (affiché une fois) */}
      {showWebhookSecretModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70" onClick={() => setShowWebhookSecretModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t("settings.webhookSecret")}</h3>
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">{t("settings.webhookSecretNotice")}</p>
            <div className="flex items-center gap-2 mb-4">
              <code className="flex-1 px-3 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white text-sm font-mono break-all">
                {webhookSecretDisplay}
              </code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(webhookSecretDisplay)}
                className="p-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white shrink-0"
                title={t("settings.copy")}
              >
                <Copy size={18} strokeWidth={2} />
              </button>
            </div>
            <button type="button" onClick={() => setShowWebhookSecretModal(false)} className="w-full py-2.5 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-slate-300 text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/15">
              {t("settings.close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
